import React from 'react';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import WorkspacesRoundedIcon from '@mui/icons-material/WorkspacesRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { formatRelativeTime, formatWorkspaceDate } from './workspaceViewModel';
import WorkspaceStatusChip, { workspaceStatusAccent } from './WorkspaceStatusChip';

const Field = ({ label, value, tooltip }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
      {label}
    </Typography>
    <Tooltip title={tooltip || value || ''} placement="top-start">
      <Typography variant="body2" noWrap sx={{ fontWeight: 650 }}>
        {value || 'Not available'}
      </Typography>
    </Tooltip>
  </Box>
);

const countLabel = (count, singular, plural = `${singular}s`) => (
  `${count} ${count === 1 ? singular : plural}`
);

export default function WorkspaceCard({ workspace, onOpen }) {
  const requestContext = workspace.requestTitle || workspace.capabilityName || 'No linked request';
  const requestTooltip = [workspace.requestTitle, workspace.capabilityName]
    .filter(Boolean)
    .join(' · ');
  const partnerScope = [workspace.partnerScopeName, workspace.partnerScopeCode]
    .filter(Boolean)
    .join(' · ') || 'Not available';
  const participants = workspace.participants.join(' · ') || 'Not available';
  const activeRunLabel = workspace.runProgress > 0
    ? `Run active · ${Math.min(workspace.runProgress, 100)}%`
    : countLabel(workspace.activeRunsCount, 'active run');

  return (
    <Card
      variant="outlined"
      sx={(theme) => ({
        height: '100%',
        borderRadius: 3,
        borderColor: theme.palette.divider,
        borderLeft: `3px solid ${workspaceStatusAccent(theme, workspace.status.key)}`,
        bgcolor: 'background.paper',
        boxShadow: theme.shadows[1],
        transition: theme.transitions.create(['box-shadow', 'border-color', 'transform'], {
          duration: theme.transitions.duration.shorter,
        }),
        '&:hover': {
          boxShadow: theme.shadows[3],
          borderColor: alpha(theme.palette.primary.main, 0.55),
          transform: 'translateY(-1px)',
        },
      })}
    >
      <CardContent sx={{ p: 2.5, height: '100%', '&:last-child': { pb: 2.5 } }}>
        <Stack spacing={2} sx={{ height: '100%' }}>
          <Stack direction="row" spacing={1.5} justifyContent="space-between" alignItems="flex-start">
            <Stack direction="row" spacing={1.25} sx={{ minWidth: 0 }}>
              <Box
                sx={(theme) => ({
                  width: 38,
                  height: 38,
                  flex: '0 0 auto',
                  display: 'grid',
                  placeItems: 'center',
                  borderRadius: 2,
                  color: 'primary.main',
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
                })}
              >
                <WorkspacesRoundedIcon fontSize="small" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Tooltip title={workspace.title} placement="top-start">
                  <Typography variant="h6" noWrap sx={{ fontWeight: 750, lineHeight: 1.25 }}>
                    {workspace.title}
                  </Typography>
                </Tooltip>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.75 }}>
                  <WorkspaceStatusChip status={workspace.status} />
                  {workspace.isCui ? <Chip size="small" color="warning" variant="outlined" label="CUI" /> : null}
                </Stack>
              </Box>
            </Stack>
            <Button
              size="small"
              variant="outlined"
              endIcon={<ArrowForwardRoundedIcon />}
              onClick={onOpen}
              aria-label={`Open ${workspace.title}`}
              sx={{ flex: '0 0 auto' }}
            >
              Open
            </Button>
          </Stack>

          {workspace.description ? (
            <Tooltip title={workspace.description} placement="top-start">
              <Typography variant="body2" color="text.secondary" noWrap>
                {workspace.description}
              </Typography>
            </Tooltip>
          ) : null}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 1.5,
            }}
          >
            <Field label="Request / Capability" value={requestContext} tooltip={requestTooltip || requestContext} />
            <Field label="Partner Scope" value={partnerScope} />
            <Field label="Participants" value={participants} />
            <Field label="Last Activity" value={formatWorkspaceDate(workspace.updatedAt)} />
          </Box>

          <Box sx={{ mt: 'auto !important' }}>
            <Divider sx={{ mb: 1.5 }} />
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                variant="outlined"
                icon={<ChatBubbleOutlineRoundedIcon />}
                label={countLabel(workspace.messagesCount, 'message')}
              />
              <Chip
                size="small"
                variant="outlined"
                icon={<AttachFileRoundedIcon />}
                label={countLabel(workspace.artifactsCount, 'artifact')}
              />
              {workspace.activeRunsCount > 0 ? (
                <Chip
                  size="small"
                  color="info"
                  icon={<BoltRoundedIcon />}
                  label={activeRunLabel}
                />
              ) : (
                <Chip
                  size="small"
                  variant="outlined"
                  icon={<BoltRoundedIcon />}
                  label={countLabel(workspace.runsCount, 'run')}
                />
              )}
              <Chip
                size="small"
                variant="outlined"
                icon={<TaskAltRoundedIcon />}
                label={countLabel(workspace.tasksCount, 'task')}
              />
              <Chip
                size="small"
                variant="outlined"
                icon={<AccessTimeRoundedIcon />}
                label={formatRelativeTime(workspace.updatedAt)}
                sx={{ color: 'text.secondary' }}
              />
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
